import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const GUEST_CART_KEY = 'guest_cart';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const prevUserRef = useRef(user);

  // Load cart on mount / auth change
  useEffect(() => {
    const loadCart = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isAuthenticated) {
          const data = await api('/cart');
          setItems(data.items || data || []);
        } else {
          const stored = localStorage.getItem(GUEST_CART_KEY);
          setItems(stored ? JSON.parse(stored) : []);
        }
      } catch (err) {
        console.error('Error loading cart:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, [isAuthenticated]);

  // Merge guest cart on login (user changes from null to object)
  useEffect(() => {
    const mergeGuestCart = async () => {
      const guestCartRaw = localStorage.getItem(GUEST_CART_KEY);
      if (!guestCartRaw) return;

      const guestItems = JSON.parse(guestCartRaw);
      if (guestItems.length === 0) return;

      try {
        for (const item of guestItems) {
          await api('/cart', {
            method: 'POST',
            body: {
              productId: item.productId,
              productSizeId: item.productSizeId,
              quantity: item.quantity,
            },
          });
        }
        localStorage.removeItem(GUEST_CART_KEY);
        // Refresh cart from server after merge
        const data = await api('/cart');
        setItems(data.items || data || []);
      } catch (err) {
        console.error('Error merging guest cart:', err);
        setError(err.message);
      }
    };

    if (prevUserRef.current === null && user !== null) {
      mergeGuestCart();
    }
    prevUserRef.current = user;
  }, [user]);

  // Persist guest cart to localStorage
  const saveGuestCart = useCallback((cartItems) => {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
  }, []);

  const addToCart = useCallback(async (productId, productSizeId, quantity = 1) => {
    setError(null);
    try {
      if (isAuthenticated) {
        const data = await api('/cart', {
          method: 'POST',
          body: { productId, productSizeId, quantity },
        });
        setItems(data.items || data || []);
      } else {
        setItems((prev) => {
          const existingIndex = prev.findIndex(
            (item) => item.productId === productId && item.productSizeId === productSizeId
          );
          let updated;
          if (existingIndex >= 0) {
            updated = prev.map((item, i) =>
              i === existingIndex
                ? { ...item, quantity: item.quantity + quantity }
                : item
            );
          } else {
            updated = [...prev, { productId, productSizeId, quantity, id: `guest_${Date.now()}` }];
          }
          saveGuestCart(updated);
          return updated;
        });
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, saveGuestCart]);

  const updateQuantity = useCallback(async (itemId, quantity) => {
    setError(null);
    try {
      if (isAuthenticated) {
        const data = await api(`/cart/${itemId}`, {
          method: 'PUT',
          body: { quantity },
        });
        setItems(data.items || data || []);
      } else {
        setItems((prev) => {
          const updated = prev.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
          );
          saveGuestCart(updated);
          return updated;
        });
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, saveGuestCart]);

  const removeItem = useCallback(async (itemId) => {
    setError(null);
    try {
      if (isAuthenticated) {
        await api(`/cart/${itemId}`, { method: 'DELETE' });
        setItems((prev) => prev.filter((item) => item.id !== itemId));
      } else {
        setItems((prev) => {
          const updated = prev.filter((item) => item.id !== itemId);
          saveGuestCart(updated);
          return updated;
        });
      }
    } catch (err) {
      console.error('Error removing item:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, saveGuestCart]);

  const clearCart = useCallback(async () => {
    setError(null);
    try {
      if (isAuthenticated) {
        await api('/cart', { method: 'DELETE' });
      } else {
        localStorage.removeItem(GUEST_CART_KEY);
      }
      setItems([]);
    } catch (err) {
      console.error('Error clearing cart:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated]);

  const itemCount = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items]
  );

  const total = useMemo(
    () => items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0),
    [items]
  );

  const value = useMemo(() => ({
    items,
    loading,
    error,
    itemCount,
    total,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
  }), [items, loading, error, itemCount, total, addToCart, updateQuantity, removeItem, clearCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
